import isNil from 'lodash/isNil';
import partition from 'lodash/partition';
import React from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Grid,
  Heading,
  Icon,
  Separator,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Stack,
  Text,
} from '@eluve/components';
import { SoapNoteOutput } from '@eluve/frontend-feature-appointment';
import { DbEnums } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import {
  dynamicArtifactTemplateSchema,
  hydrateDynamicArtifactTemplate,
} from '@eluve/llm-outputs';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';
import { toSentences } from '@eluve/utils';

export const inputResultsFragment = graphql(`
  fragment inputResults on EvalFactVerificationInputs @_unmask {
    __typename
    id
    sourceArtifactId
    artifact {
      __typename
      id
      name
      content
    }
    results {
      __typename
      id
      isCorrectlyIdentified
      supportingEvidence
      sourceFact {
        __typename
        id
        statement
        expectedOutput
      }
    }
    input_llm_outputs {
      __typename
      output {
        __typename
        id
        content
        metadata {
          __typename
          llmOutputId
          prompt_template {
            __typename
            id
            outputType
          }
          output_variant {
            __typename
            outputTemplateId
            variantNumber
            template
          }
        }
      }
    }
  }
`);

export interface FactVerificationInputResultsListItemProps {
  id: string;
}

export const FactVerificationInputResultsListItem: React.FC<
  FactVerificationInputResultsListItemProps
> = ({ id }) => {
  const data = useCompleteFragment({
    fragment: inputResultsFragment,
    key: {
      id,
    },
  });

  const results = data.results;

  const inputLlmOutput = data.input_llm_outputs?.[0]?.output;

  const content = inputLlmOutput?.content ?? {};

  const isDynamicOutput =
    inputLlmOutput?.metadata?.prompt_template?.outputType ===
    DbEnums.LlmOutputType.DYNAMIC_OUTPUT;
  const outputTemplate = inputLlmOutput?.metadata?.output_variant?.template;
  const outputTemplateParsed =
    isDynamicOutput && outputTemplate
      ? dynamicArtifactTemplateSchema.parse(outputTemplate)
      : null;

  const [unknown, rest] = partition(results, (r) =>
    isNil(r.isCorrectlyIdentified),
  );

  const [passed, failed] = partition(rest, (r) => r.isCorrectlyIdentified);

  return (
    <div className="w-full border border-border-primary p-2">
      <Sheet>
        <Stack direction="horizontal" align="center" gap={2}>
          <SheetTrigger asChild>
            <Button variant="outline">
              <Icon name="MessageSquareQuote" size="xl" />
            </Button>
          </SheetTrigger>
          <Heading size="lg" weight="semibold">
            {data.artifact.name}
          </Heading>
        </Stack>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>{data.artifact.name}</SheetTitle>
          </SheetHeader>
          <div className="container max-h-[768px] overflow-y-auto">
            <Text className="break-words whitespace-pre-line">
              {toSentences(data.artifact.content)}
            </Text>
          </div>
        </SheetContent>
      </Sheet>
      <Stack className="mt-2" direction="horizontal" align="center" gap={2}>
        <Badge variant="success">
          <Icon name="CheckCheck" size="lg" className="mr-2" /> {passed.length}{' '}
          Passed
        </Badge>
        <Badge variant="destructive">
          <Icon name="X" size="lg" className="mr-2" />
          {failed.length} Failed
        </Badge>
        {unknown.length > 0 && (
          <Badge variant="info">{unknown.length} Pending</Badge>
        )}
      </Stack>
      <Collapsible>
        <CollapsibleTrigger>
          <Stack direction="horizontal" align="end" gap={1} fullWidth={false}>
            <Icon name="ChevronDown" size="xl" />
            <Text className="mt-2 underline">Expand Detailed Results</Text>
          </Stack>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Grid columns={2} gap={4} className="relative pt-2">
            <div className="border p-4 shadow-md">
              {isDynamicOutput ? (
                <DynamicArtifactRenderer
                  isReadonly={true}
                  blocks={
                    hydrateDynamicArtifactTemplate(
                      outputTemplateParsed!,
                      content ?? {},
                    ).blocks
                  }
                />
              ) : (
                <SoapNoteOutput
                  content={data.input_llm_outputs?.[0]?.output?.content ?? {}}
                />
              )}
            </div>
            <div>
              <Stack direction="horizontal" fullWidth={false} gap={2}>
                <Icon name="FlaskConical" size="xl" />
                <Heading size="xl" weight="semibold">
                  Fact Verification Results
                </Heading>
              </Stack>
              <Separator className="my-2" />
              <ul className="divide-y divide-border-primary px-4">
                {results.map((r) => {
                  const expected = r.sourceFact.expectedOutput;
                  const observed = r.isCorrectlyIdentified
                    ? expected
                    : !expected;

                  return (
                    <Stack className="p-2" key={r.id} fullWidth={false} gap={2}>
                      <ResultBadge pass={r.isCorrectlyIdentified} />
                      <Stack direction="horizontal" fullWidth={false} gap={2}>
                        <Text weight="bold">Fact:</Text>
                        <Text>{r.sourceFact.statement}</Text>
                      </Stack>
                      {r.isCorrectlyIdentified !== null && (
                        <Stack
                          align="stretch"
                          direction="horizontal"
                          fullWidth={false}
                          gap={2}
                        >
                          <Text weight="bold">Evidence:</Text>
                          <Text>{r.supportingEvidence}</Text>
                        </Stack>
                      )}
                      <Stack direction="horizontal" fullWidth={false} gap={2}>
                        <Text weight="bold">Expected:</Text>
                        <ExpectedBadge isTrue={expected} />
                        {r.isCorrectlyIdentified !== null && (
                          <>
                            <Text weight="bold" className="ml-2">
                              Observed:
                            </Text>
                            <ExpectedBadge isTrue={observed} />
                          </>
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </ul>
            </div>
          </Grid>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const ResultBadge: React.FC<{ pass: boolean | null }> = ({ pass }) => {
  if (pass === null) {
    return (
      <Badge variant="secondary">
        <Icon name="CircleHelp" className="mr-2 -ml-1" />
        PENDING
      </Badge>
    );
  }
  if (pass) {
    return (
      <Badge variant="success">
        <Icon name="Check" className="mr-1 -ml-1" />
        PASS
      </Badge>
    );
  }
  return (
    <Badge variant="destructive">
      <Icon name="X" className="mr-1 -ml-1" />
      FAIL
    </Badge>
  );
};

const ExpectedBadge: React.FC<{ isTrue: boolean }> = ({ isTrue }) => {
  if (isTrue) {
    return (
      <Text weight="semibold" className="text-bg-lightgreen-strong">
        True
      </Text>
    );
  }
  return (
    <Text weight="semibold" className="text-bg-negative-strong-primary">
      False
    </Text>
  );
};
